
import store from '@/store';
import Vue from 'vue'
import i18n from '@/plugins/i18n'
import { translateUser, translateRecordValues } from '@/utils/translator'
import { BlobDto, Demand, FacetDto, ProfileDemandsAndSuppliesUpdateRequest, ProfileUpdatedRequest, Supply, UserDto } from '@chainsight/networker-backend-axios-sdk';

export default Vue.extend({
	name: 'UserProfile',
	data() {
		return {
			overlay: false,
			isAnalytics: false,
			isKeywordAnalyzing: false,
			progressValue: 0,
			progressStatusText: '',
			interval: null as any,
			indexForEdit: -1,
			editDemandDialog: false,
			editSupplyDialog: false,
			addDemandDialog: false,
			addSupplyDialog: false,
			industrySearchText:'',
			industryFilteredList: [] as string[],
			showEditButton: null as string | null,
			editProfileDialog: false,
			editServicesDialog: false,
			isProfilePublic: true,
			userInfo: {
				fullName: '',
				jobTitle: '',
				currentEmail: '',
				phone: '',
				companyName: '',
				companyUrl: '',
				industry: [] as string[],
				location: '',
				bioDescription: '',
				linkedinUrl: ''
			},
			editingUserInfo: {
				fullName: '',
				jobTitle: '',
				currentEmail: '',
				phone: '',
				companyName: '',
				companyUrl: '',
				industry: [] as string[],
				location: '',
				bioDescription: '',
				linkedinUrl: ''
			},
			facetDto: {
				industries:[] as string[]
			} as FacetDto,
			userDto: {} as UserDto,
			editingSupplies: [] as Supply[],
			editingDemands: [] as Demand[],
			demandForEdit: {} as Demand,
			supplyForEdit: {} as Supply,
			supplyForAdd: {} as Supply,
			demandForAdd: {} as Demand,
			avatarBlobDto: null as unknown as BlobDto,
			pdfBlobDto: null as unknown as BlobDto,
			industryKeyValue: {} as Record<string, string>,
			translatedToOriginalIndustryMap: {} as Record<string, string>, // 翻譯語言 -> 英文原文
			allIndustries: [] as string[],
			file: null as File | null,
			pdfFile: null as File | null,
			pdfFileName: '',
		};
	},
	computed: {
		trackDto(){
			return store.state.track.trackDto
		},
		user() {
			return store.state.user;
		},
		blobBaseUrl() {
			return process.env.VUE_APP_BLOB_API_BASE_URL;
		},
		industryItemsLimited(): { text: string; value: string; disabled: boolean }[] {
			const industryList: string[] = Array.isArray(this.editingUserInfo.industry) ? this.editingUserInfo.industry : [];
			const isMax = industryList.length >= 5;
			const baseList: string[] = this.industrySearchText?.length > 0
				? (this.industryFilteredList ?? []) : (this.allIndustries || []);

			return baseList.map((item: string) => ({
				text: item,
				value: item,
				disabled: isMax && !this.editingUserInfo.industry.includes(item),
			}));
		},
		canUseAIAnalysis(): boolean {
			const hasCompanyName = !!(this.userDto?.companyName && this.userDto.companyName.trim());
			const hasCompanyUrl = !!(this.userDto?.companyUrl && this.userDto.companyUrl.trim());
			const hasPdfFile = !!(this.userDto as any)?.pdfBlobId;
			
			return hasCompanyName || hasCompanyUrl || hasPdfFile;
		},
		hasPdfFile(): boolean {
			return !!(this.userDto as any)?.pdfBlobId;
		},
		isProfileFormValid(): boolean {
			const requiredFields = [
				this.editingUserInfo.fullName,
				this.editingUserInfo.jobTitle,
				this.editingUserInfo.currentEmail,
				this.editingUserInfo.companyName
			];

			return requiredFields.every(field => field && field.trim().length > 0);
		}
	},
	methods: {
		goBack() {
			// 檢查是否有上一頁歷史記錄
			if (window.history.length > 1) {
				// 獲取當前網站的 domain
				const currentDomain = window.location.hostname;

				// 嘗試返回上一頁
				const previousUrl = document.referrer;

				// 如果有 referrer 且來自同一 domain，則返回上一頁
				if (previousUrl) {
					const previousDomain = new URL(previousUrl).hostname;
					if (previousDomain === currentDomain) {
						this.$router.go(-1);
					} else {
						// 如果來自不同 domain，回到首頁
						this.$router.push('/');
					}
				} else {
					// 沒有 referrer 的情況下，嘗試返回，如果失敗則回到首頁
					try {
						this.$router.go(-1);
					} catch {
						this.$router.push('/');
					}
				}
			} else {
				// 沒有歷史記錄，直接回到首頁
				this.$router.push('/');
			}
		},
		async getMe() {
			let getMeResp
			try{
				getMeResp = await this.api$.authApi.getMe()
				this.userDto = getMeResp.data.data
				this.userInfo.bioDescription = this.userDto.bioDescription as string
				this.userInfo.currentEmail = this.userDto.currentEmail as string
				this.userInfo.phone = this.userDto.phone as string
				this.userInfo.companyName = this.userDto.companyName as string
				this.userInfo.companyUrl = this.removeUrlPrefix(this.userDto.companyUrl || '')
				this.userInfo.fullName = (this.userDto.firstName || this.userDto.lastName)
											? [this.userDto.firstName, this.userDto.lastName].filter(Boolean).join(' ')
											: ''
				this.userInfo.jobTitle = this.userDto.companyTitle?.join(', ') as string
				this.userInfo.location = this.userDto.companyLocation as string
				this.userInfo.linkedinUrl = this.removeUrlPrefix(this.userDto.linkedinUrl || '')
				this.userInfo.industry = this.userDto.companyIndustry as string[] // 保留英文，稍後在 created() 中轉換

				// 如果有已上傳的PDF，設定檔案名稱
				if ((this.userDto as any).pdfBlobId && (this.userDto as any).pdfBlob) {
					this.pdfFileName = (this.userDto as any).pdfBlob.fileName || this.$t('userProfile.misc.pdfUploaded')
				}

				// 初始化公開狀態
				this.isProfilePublic = this.userDto.isDelete !== true;

				if (i18n.locale === 'zh') {
					await translateUser(this.userDto || [], 'zh-Hant')
				} else if (i18n.locale === 'en') {
					await translateUser(this.userDto || [], 'en-US')
				} else if (i18n.locale === 'ja') {
					await translateUser(this.userDto || [], 'ja-JP')
				}
			}catch (error) {
				console.error(error)
			}
		},
		triggerFileUpload() {
			(this.$refs.fileInput as HTMLInputElement)?.click()
		},
		async handleFileChange(event) {
			this.overlay = true
			const file = event.target.files[0]
			if (file) {
				console.log('File selected:', file)

				this.file = file

				let createBlobResp
				try{
					createBlobResp = await this.api$.blobApi.createBlob(file)
					this.avatarBlobDto = createBlobResp?.data?.data

					await this.updateUserProfile()
				}catch (error) {
					console.error(error)
				}
			}
			this.overlay = false
		},
		triggerPdfUpload() {
			(this.$refs.pdfInput as HTMLInputElement)?.click()
		},
		handlePdfUpload(event: Event) {
			const target = event.target as HTMLInputElement
			const file = target.files?.[0]
			if (file && file.type === 'application/pdf') {
				this.pdfFile = file
				this.pdfFileName = file.name
			}
		},
		clearPdf() {
			this.pdfFile = null
			this.pdfFileName = '';
			(this.userDto as any).pdfBlobId = null;
			(this.userDto as any).pdfBlob = null
		},
		async updateUserProfile(){
			let createBlobResp
			if (this.pdfFile) {
				try {
					createBlobResp = await this.api$.blobApi.createBlob(this.pdfFile)
					this.pdfBlobDto = createBlobResp?.data?.data
				} catch (error) {
					console.error('PDF upload error:', error)
				}
			}

			// 分離名字和姓氏
			const nameParts = this.userInfo.fullName.trim().split(' ')
			const firstName = nameParts[0] || ''
			const lastName = nameParts.slice(1).join(' ') || ''

			// 分離職位
			const titles = this.userInfo.jobTitle ? this.userInfo.jobTitle.split(',').map(t => t.trim()).filter(t => t) : []

			// 將翻譯後的行業名稱轉換回英文原文
			const industryInEnglish = this.userInfo.industry?.map(translatedIndustry => {
				// 如果能找到對應的英文原文，使用英文；否則保持原值
				return this.translatedToOriginalIndustryMap[translatedIndustry] || translatedIndustry
			}) || []

			const profileUpdatedRequest: ProfileUpdatedRequest = {
				firstName: this.userInfo.fullName,
				lastName: '',
				bioDescription: this.userInfo.bioDescription,
				companyName: this.userInfo.companyName,
				companyUrl: this.processUrl(this.userInfo.companyUrl),
				companyIndustry: industryInEnglish,
				companyTitle: this.userInfo.jobTitle?.trim()
							? this.userInfo.jobTitle.split(',').map(t => t.trim()).filter(Boolean)
							: undefined,
				companyLocation: this.userInfo.location,
				linkedinUrl: this.processUrl(this.userInfo.linkedinUrl),
				avatarBlobId: this.avatarBlobDto ? this.avatarBlobDto.id : undefined,
				pdfBlobId: this.pdfFile ? this.pdfBlobDto.id : (this.userDto.pdfBlobId ? this.userDto.pdfBlobId : undefined),
				currentEmail: this.userInfo.currentEmail,
				phone: this.userInfo.phone,
				demands: this.userDto.demands,
				supplies: this.userDto.supplies,
				productTrackId: this.trackDto?.id,
				isDelete: this.userDto?.isDelete,
			}

			let updateUserProfileResp
			try {
				// 儲存更新前的狀態
				const beforeIsPaidMember = this.userDto?.isPaidMember || false
				const beforeIsFreeUpgradeUsed = this.userDto?.isFreeUpgradeUsed || false

				updateUserProfileResp = await this.api$.profileApi.updateProfile(this.userDto?.id as string, profileUpdatedRequest)
				this.userDto = updateUserProfileResp.data.data

				// 更新 store 中的 userDto
				store.commit('SET_USER_DTO', this.userDto)

				this.userInfo.bioDescription = this.userDto.bioDescription as string
				this.userInfo.currentEmail = this.userDto.currentEmail as string
				this.userInfo.phone = this.userDto.phone as string
				this.userInfo.companyName = this.userDto.companyName as string
				this.userInfo.companyUrl = this.removeUrlPrefix(this.userDto.companyUrl || '')
				this.userInfo.fullName = (this.userDto.firstName || this.userDto.lastName)
											? [this.userDto.firstName, this.userDto.lastName].filter(Boolean).join(' ')
											: ''
				this.userInfo.jobTitle = this.userDto.companyTitle?.join(', ') as string
				this.userInfo.location = this.userDto.companyLocation as string
				this.userInfo.linkedinUrl = this.removeUrlPrefix(this.userDto.linkedinUrl || '')

				// 如果有已上傳的PDF，設定檔案名稱
				if ((this.userDto as any).pdfBlobId && (this.userDto as any).pdfBlob) {
					this.pdfFileName = (this.userDto as any).pdfBlob.fileName || this.$t('userProfile.misc.pdfUploaded')
				}

				// 初始化公開狀態
				this.isProfilePublic = this.userDto.isDelete !== true;

				if (i18n.locale === 'zh') {
					await translateUser(this.userDto || [], 'zh-Hant')
				} else if (i18n.locale === 'en') {
					await translateUser(this.userDto || [], 'en-US')
				} else if (i18n.locale === 'ja') {
					await translateUser(this.userDto || [], 'ja-JP')
				}

				// 將英文的行業名稱轉換為當前語言
				this.userInfo.industry = this.convertIndustryToCurrentLanguage(this.userDto.companyIndustry as string[])

				// 同時更新編輯狀態的資料
				this.editingUserInfo = { ...this.userInfo };

				// 檢查是否從 false 變成 true
				const afterIsPaidMember = this.userDto?.isPaidMember || false
				const afterIsFreeUpgradeUsed = this.userDto?.isFreeUpgradeUsed || false

				if (!beforeIsPaidMember && afterIsPaidMember && !beforeIsFreeUpgradeUsed && afterIsFreeUpgradeUsed) {
					// 觸發事件顯示 free trial banner
					this.eventHub$.$emit('showFreeTrialBanner')
					// 同時觸發 AvatarMenu 更新會員狀態
					this.eventHub$.$emit('avatarMenuGetUser')
				}
			}catch (error) {
				console.error(error)
			}
		},
		async enhanceProfile() {
			// 關閉 dialog 並開始 AI 分析
			this.editServicesDialog = false;
			this.isAnalytics = true;
			this.overlay = true;
			
			let enhanceUserProfileResp
			try{
				enhanceUserProfileResp = await this.api$.profileApi.enhanceUserProfile(this.user.userDto?.id as string, '0')
				this.userDto = enhanceUserProfileResp.data.data

				if (i18n.locale === 'zh') {
					await translateUser(this.userDto || [], 'zh-Hant')
				} else if (i18n.locale === 'en') {
					await translateUser(this.userDto || [], 'en-US')
				} else if (i18n.locale === 'ja') {
					await translateUser(this.userDto || [], 'ja-JP')
				}
				
				// // AI 分析完成後，重新開啟服務與需求編輯 dialog
				// setTimeout(() => {
				// 	this.editServicesDialog = true;
				// }, 500); // 短暫延遲讓用戶看到更新效果
				
			}catch (error) {
				console.error(error)
			}
			
			this.overlay = false
			this.isAnalytics = false
		},
		async getFilterList() {
			let getFilterListResp
			try{
				getFilterListResp = await this.api$.platformApi.getFilterList()
				this.facetDto = getFilterListResp.data.data

				this.industryKeyValue = this.normalizeToKeyValue(this.facetDto.industries) ?? {}

				if (i18n.locale === 'zh') {
					await Promise.all([
						translateRecordValues(this.industryKeyValue, 'zh-Hant'),
					])
				} else if (i18n.locale === 'en') {
					await Promise.all([
						translateRecordValues(this.industryKeyValue, 'en-US'),
					])
				} else if (i18n.locale === 'ja') {
					await Promise.all([
						translateRecordValues(this.industryKeyValue, 'ja-JP'),
					])
				}

				this.allIndustries = Object.values(this.industryKeyValue ?? {})

				// 建立反向 map：翻譯後的文字 -> 英文原文
				this.translatedToOriginalIndustryMap = {}
				for (const [originalKey, translatedValue] of Object.entries(this.industryKeyValue)) {
					this.translatedToOriginalIndustryMap[translatedValue] = originalKey
				}
			}catch (error) {
				console.error(error)
			}
		},
		normalizeToKeyValue(data: any): Record<string, string> | undefined {
			if (Array.isArray(data)) {
				return data.reduce((acc: Record<string, string>, item: any) => {
					if (typeof item === 'string') {
						acc[item] = item;
					}
					return acc;
				}, {});
			}
			return undefined;
		},
		// 將英文行業名稱轉換為當前語言的翻譯版本
		convertIndustryToCurrentLanguage(englishIndustries: string[]): string[] {
			if (!englishIndustries || englishIndustries.length === 0) {
				return []
			}

			return englishIndustries.map(englishIndustry => {
				// 從 industryKeyValue 找到對應的翻譯
				// industryKeyValue 的 key 是英文原文，value 是翻譯後的文字
				return this.industryKeyValue[englishIndustry] || englishIndustry
			})
		},
		normalizedWebsite(url: string): string {
			if (!url) return '';
			if (url.startsWith('http://') || url.startsWith('https://')) {
				return url;
			}
			return 'https://' + url;
		},
		processUrl(url: string): string {
			if (!url || url.trim() === '') return '';
			
			// 移除可能重複的 https:// 前綴
			let processedUrl = url.trim();
			if (processedUrl.startsWith('https://https://')) {
				processedUrl = processedUrl.replace('https://https://', 'https://');
			} else if (processedUrl.startsWith('http://https://')) {
				processedUrl = processedUrl.replace('http://https://', 'https://');
			} else if (!processedUrl.startsWith('http://') && !processedUrl.startsWith('https://')) {
				processedUrl = 'https://' + processedUrl;
			}
			
			return processedUrl;
		},
		removeUrlPrefix(url: string): string {
			if (!url) return '';
			// 移除 https:// 或 http:// 前綴用於編輯表單顯示
			return url.replace(/^https?:\/\//, '');
		},
		openEditProfileDialog() {
			// 複製當前資料到編輯狀態
			this.editingUserInfo = { ...this.userInfo };
			this.editProfileDialog = true;
		},
		async saveProfileChanges() {
			// 驗證表單
			const isValid = (this.$refs.profileForm as any).validate();
			
			// 如果驗證失敗，不執行儲存
			if (!isValid) {
				return;
			}
			
			// 顯示 loading 並關閉 dialog
			this.overlay = true;
			this.editProfileDialog = false;
			
			// 更新實際的 userInfo
			this.userInfo = { ...this.editingUserInfo };
			await this.updateUserProfile();
			
			// 隱藏 loading
			this.overlay = false;
		},
		cancelProfileChanges() {
			// 不儲存變更，直接關閉
			this.editProfileDialog = false;
		},
		openEditServicesDialog() {
			// 複製當前資料到編輯狀態
			this.editingSupplies = this.userDto.supplies ? JSON.parse(JSON.stringify(this.userDto.supplies)) : [];
			this.editingDemands = this.userDto.demands ? JSON.parse(JSON.stringify(this.userDto.demands)) : [];
			this.editServicesDialog = true;
		},
		async saveServicesChanges() {
			// 顯示 loading 並關閉 dialog
			this.overlay = true;
			this.editServicesDialog = false;
			
			try {
				// 呼叫 API 更新服務與需求
				const profileDemandsAndSuppliesUpdateRequest: ProfileDemandsAndSuppliesUpdateRequest = {
					demands: this.editingDemands,
					supplies: this.editingSupplies
				};
				
				await this.api$.profileApi.updateDemandsAndSupplies(
					this.userDto?.id as string, 
					profileDemandsAndSuppliesUpdateRequest
				);
				
				// 更新實際資料
				this.userDto.supplies = JSON.parse(JSON.stringify(this.editingSupplies));
				this.userDto.demands = JSON.parse(JSON.stringify(this.editingDemands));
				
				// 翻譯更新後的資料
				if (i18n.locale === 'zh') {
					await translateUser(this.userDto || [], 'zh-Hant');
				} else if (i18n.locale === 'en') {
					await translateUser(this.userDto || [], 'en-US');
				} else if (i18n.locale === 'ja') {
					await translateUser(this.userDto || [], 'ja-JP');
				}
				
			} catch (error) {
				console.error('Error updating services and demands:', error);
			}
			
			// 隱藏 loading
			this.overlay = false;
		},
		cancelServicesChanges() {
			// 不儲存變更，直接關閉
			this.editServicesDialog = false;
		},
		editSupply(index: number) {
			this.indexForEdit = index;
			this.supplyForEdit = { ...this.editingSupplies[index] };
			this.editSupplyDialog = true;
		},
		deleteSupplyFromEditing(index: number) {
			this.editingSupplies.splice(index, 1);
		},
		deleteDemandFromEditing(index: number) {
			this.editingDemands.splice(index, 1);
		},
		updateSupply(supply: Supply, index: number) {
			if (index >= 0 && index < this.editingSupplies.length) {
				this.$set(this.editingSupplies, index, supply);
				this.editSupplyDialog = false;
			}
		},
		updateDemand(demand: Demand, index: number) {
			if (index >= 0 && index < this.editingDemands.length) {
				this.$set(this.editingDemands, index, demand);
				this.editDemandDialog = false;
			}
		},
		addSupplyToEditing() {
			this.editingSupplies.push(JSON.parse(JSON.stringify(this.supplyForAdd)));
			this.supplyForAdd = { description: '' } as Supply;
			this.addSupplyDialog = false;
		},
		addDemandToEditing() {
			this.editingDemands.push(JSON.parse(JSON.stringify(this.demandForAdd)));
			this.demandForAdd = { description: '' } as Demand;
			this.addDemandDialog = false;
		},
		industryFilter() {
			const searchLower = this.industrySearchText.toLowerCase();
			this.industryFilteredList = this.allIndustries?.filter(item => 
				item.toLowerCase().includes(searchLower)
			) as string[];
		},
		async addUnitFromProfile(){
			this.overlay = true
			let addUnitFromUserProfileResp
			try{
				// isProfilePublic: true = 公開 (isDelete: false)
				// isProfilePublic: false = 不公開 (isDelete: true)
				const isDelete = !this.isProfilePublic
				addUnitFromUserProfileResp = await this.api$.profileApi.addUnitFromUserProfile(this.user.userDto?.id as string, '0', isDelete)
				this.userDto.isDelete = addUnitFromUserProfileResp.data.data.isDelete
			} catch (error) {
				console.error(error)
			}
			this.overlay = false
		},
	},
	async created() {
		this.overlay = true
		await Promise.all([
			this.getMe(),
			this.getFilterList()
		])

		// 在 getFilterList() 完成後，將英文的行業名稱轉換為當前語言
		if (this.userDto.companyIndustry) {
			this.userInfo.industry = this.convertIndustryToCurrentLanguage(this.userDto.companyIndustry as string[])
			this.editingUserInfo = { ...this.userInfo }
		}

		this.overlay = false
	},
	mounted() {
		// 監聽搜尋事件
		this.eventHub$.$on('searchStarted', () => {
			this.isKeywordAnalyzing = true
			this.progressValue = 0
			this.progressStatusText = String(this.$t('common.progressStatus.analyzingRequest'))
			
			// 啟動進度條動畫 - 15秒分成4等份
			let timer = 0
			this.interval = setInterval(() => {
				timer += 0.1  // 每100ms增加0.1秒
				
				// 確保不超過15秒
				if (timer > 15) {
					timer = 15
				}
				
				if (timer <= 3.75) {
					// 第一等分：分析您的需求
					this.progressStatusText = String(this.$t('common.progressStatus.analyzingRequest'))
					this.progressValue = (timer / 3.75) * 24.75  // 0-24.75%
				} else if (timer > 3.75 && timer <= 7.5) {
					// 第二等分：搜尋相關成員
					this.progressStatusText = String(this.$t('common.progressStatus.searchingMembers'))
					this.progressValue = 24.75 + ((timer - 3.75) / 3.75) * 24.75  // 24.75-49.5%
				} else if (timer > 7.5 && timer <= 11.25) {
					// 第三等分：智慧配對中
					this.progressStatusText = String(this.$t('common.progressStatus.smartMatching'))
					this.progressValue = 49.5 + ((timer - 7.5) / 3.75) * 24.75  // 49.5-74.25%
				} else if (timer > 11.25 && timer <= 15) {
					// 第四等分：生成結果
					this.progressStatusText = String(this.$t('common.progressStatus.generatingResults'))
					this.progressValue = 74.25 + ((timer - 11.25) / 3.75) * 24.75  // 74.25-99%
				}
			}, 100)
		})

		this.eventHub$.$on('searchFinished', () => {
			// 完成動畫
			this.progressValue = 100
			clearInterval(this.interval)
			setTimeout(() => {
				this.isKeywordAnalyzing = false
				this.progressValue = 0
				this.progressStatusText = ''
			}, 500)
		})
	},
	beforeDestroy() {
		// 清理事件監聽器
		this.eventHub$.$off('searchStarted')
		this.eventHub$.$off('searchFinished')
		if (this.interval) {
			clearInterval(this.interval)
		}
	},
	watch: {
		'$i18n.locale': {
			immediate: false,
			async handler() {
				this.overlay = true
				if (i18n.locale === 'zh') {
					await Promise.all([
						translateUser(this.userDto || [], 'zh-Hant'),
						translateRecordValues(this.industryKeyValue, 'zh-Hant')
					])
				} else if (i18n.locale === 'en') {
					await Promise.all([
						translateUser(this.userDto || [], 'en-US'),
						translateRecordValues(this.industryKeyValue, 'en-US')
					])
				} else if (i18n.locale === 'ja') {
					await Promise.all([
						translateUser(this.userDto || [], 'ja-JP'),
						translateRecordValues(this.industryKeyValue, 'ja-JP')
					])
				}

				// 更新 allIndustries 以反映翻譯後的值
				await this.getMe()
				await this.getFilterList()

				// 語言切換後，將英文的行業名稱轉換為新語言並更新 editingUserInfo
				if (this.userDto.companyIndustry) {
					this.userInfo.industry = this.convertIndustryToCurrentLanguage(this.userDto.companyIndustry as string[])
					this.editingUserInfo = { ...this.userInfo }
				}

				this.overlay = false
			}
		}
	}
});
